/* ===========================================================================
   WARA account store — the ONLY module that touches client-side persistence.

   Storage doctrine (v1): likes and measurements live in localStorage. Session C
   replaces the internals of THIS FILE with customer-metafield calls and nothing
   outside it changes. That only holds while no other module reaches for
   localStorage directly, so don't add a second reader anywhere.

   Mutations are Promise-shaped from day one for the same reason: a synchronous
   v1 would force every call site to change the moment a real backend lands.
   Render paths that can't await use the *Sync readers, served from an in-memory
   cache that a future backend hydrates once per page load behind `ready`.

   Keys
     wara:likes         [{ handle, liked_at }]  liked_at is ISO8601, kept as
                                                demand-signal data, not display.
     wara:measurements  {}                      schema owned by Session B.
   =========================================================================== */
(function () {
  'use strict';

  var LIKES_KEY = 'wara:likes';
  var MEAS_KEY = 'wara:measurements';

  var likesCache = null;
  var measCache = null;
  var handlers = {};

  /* localStorage throws in Safari private mode and when over quota. Every access
     is wrapped: a store that cannot persist must still serve reads, so failure
     degrades to in-memory-only rather than breaking the UI. */
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); return true; } catch (e) { return false; }
  }

  function parse(raw, fallback) {
    if (!raw) return fallback;
    try {
      var v = JSON.parse(raw);
      return (v === null || v === undefined) ? fallback : v;
    } catch (e) { return fallback; }
  }

  /* Tolerant of a bare ["handle"] array — a hand-seeded value or a future
     rollback — and drops duplicates so toggle() can never desync. */
  function normalize(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    var seen = Object.create(null);
    for (var i = 0; i < raw.length; i++) {
      var entry = raw[i];
      var handle = (typeof entry === 'string') ? entry : (entry && entry.handle);
      if (!handle || seen[handle]) continue;
      seen[handle] = true;
      out.push({ handle: String(handle), liked_at: (entry && entry.liked_at) || null });
    }
    return out;
  }

  function likes() {
    if (likesCache === null) likesCache = normalize(parse(lsGet(LIKES_KEY), []));
    return likesCache;
  }

  function indexOf(handle) {
    var all = likes();
    for (var i = 0; i < all.length; i++) {
      if (all[i].handle === handle) return i;
    }
    return -1;
  }

  function listSync() {
    return likes().map(function (e) {
      return { handle: e.handle, liked_at: e.liked_at };
    });
  }
  function hasSync(handle) { return indexOf(handle) !== -1; }
  function countSync() { return likes().length; }

  function emit(name, detail) {
    var subs = handlers[name] || [];
    for (var i = 0; i < subs.length; i++) {
      try { subs[i](detail); } catch (e) { console.error('[wara-store] handler failed', e); }
    }
    /* Also a DOM event so a section can react without holding a module ref.
       UI code listens to this one only, to avoid double work. */
    try {
      document.dispatchEvent(new CustomEvent('wara:' + name, { detail: detail }));
    } catch (e) { /* no CustomEvent: module handlers above already ran */ }
  }

  function commit() {
    lsSet(LIKES_KEY, JSON.stringify(likes()));
    emit('likes:changed', { likes: listSync(), count: countSync() });
  }

  function add(handle) {
    if (!handle) return Promise.resolve(false);
    if (indexOf(handle) === -1) {
      // Newest first: the saved page renders in stored order.
      likes().unshift({ handle: String(handle), liked_at: new Date().toISOString() });
      commit();
    }
    return Promise.resolve(true);
  }

  function remove(handle) {
    var i = indexOf(handle);
    if (i !== -1) {
      likes().splice(i, 1);
      commit();
    }
    return Promise.resolve(false);
  }

  // Resolves to the RESULTING liked state, so callers never re-read to find out.
  function toggle(handle) {
    return hasSync(handle) ? remove(handle) : add(handle);
  }

  /* Silent prune for handles the storefront no longer renders (deleted or
     unpublished products). Callers must pass only confirmed-dead handles — a
     failed fetch is not evidence of death. Resolves with what was dropped. */
  function prune(handles) {
    if (!handles || !handles.length) return Promise.resolve([]);
    var dropped = [];
    for (var i = 0; i < handles.length; i++) {
      var j = indexOf(handles[i]);
      if (j !== -1) {
        dropped.push(handles[i]);
        likes().splice(j, 1);
      }
    }
    if (dropped.length) commit();
    return Promise.resolve(dropped);
  }

  function measurements() {
    if (measCache === null) {
      var v = parse(lsGet(MEAS_KEY), {});
      measCache = (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
    }
    return measCache;
  }

  /* Cross-tab: a like in one tab should show up in another. Invalidate and
     re-emit rather than trusting e.newValue, which is null after a clear(). */
  window.addEventListener('storage', function (e) {
    if (e.key === LIKES_KEY) {
      likesCache = null;
      emit('likes:changed', { likes: listSync(), count: countSync() });
    } else if (e.key === MEAS_KEY) {
      measCache = null;
      emit('measurements:changed', { measurements: measurements() });
    }
  });

  window.WARAStore = {
    /* Resolves once the store can answer reads. Trivial today; Session C turns
       it into a real fetch, so await it before any non-Sync read on a cold page. */
    ready: Promise.resolve(),
    version: 1,

    likes: {
      list: function () { return Promise.resolve(listSync()); },
      listSync: listSync,
      has: function (h) { return Promise.resolve(hasSync(h)); },
      hasSync: hasSync,
      count: function () { return Promise.resolve(countSync()); },
      countSync: countSync,
      add: add,
      remove: remove,
      toggle: toggle,
      prune: prune
    },

    // Schema deliberately unopinionated — Session B defines the field set.
    measurements: {
      get: function () { return Promise.resolve(measurements()); },
      getSync: measurements,
      set: function (obj) {
        measCache = (obj && typeof obj === 'object' && !Array.isArray(obj)) ? obj : {};
        lsSet(MEAS_KEY, JSON.stringify(measCache));
        emit('measurements:changed', { measurements: measCache });
        return Promise.resolve(measCache);
      }
    },

    on: function (name, fn) {
      (handlers[name] = handlers[name] || []).push(fn);
      return function off() {
        var subs = handlers[name] || [];
        var i = subs.indexOf(fn);
        if (i !== -1) subs.splice(i, 1);
      };
    }
  };
})();
